import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API, eur, formatApiErrorDetail, RATE_CODES, RATE_LABELS, CATEGORY_TO_BUCKET } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  ScanLine, Upload, Camera, Store, CalendarDays, CheckCircle2, Clock, Trash2,
  Grid2x2, BookText, AlertTriangle, ShieldCheck, Building2, ExternalLink, FileText, Mail, Settings, RefreshCw,
  ZoomIn, ZoomOut, Maximize2, Send, CalendarClock,
} from "lucide-react";

const conf = { high: "text-emerald-700 border-emerald-300", medium: "text-amber-600 border-amber-300", low: "text-red-600 border-red-300" };

function Thumb({ docId, filename }) {
  const [url, setUrl] = useState(null);
  const isPdf = (filename || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    if (isPdf) return;
    let revoked = null;
    const token = localStorage.getItem("token");
    fetch(`${API}/documents/${docId}/file`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { revoked = URL.createObjectURL(b); setUrl(revoked); })
      .catch(() => {});
    return () => revoked && URL.revokeObjectURL(revoked);
  }, [docId, isPdf]);
  if (isPdf) {
    return <div className="h-16 w-16 shrink-0 rounded-md border border-border bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground">PDF</div>;
  }
  return url
    ? <img src={url} alt="Z-ticket" className="h-16 w-16 shrink-0 rounded-md border border-border object-cover" />
    : <div className="h-16 w-16 shrink-0 rounded-md border border-border bg-muted animate-pulse" />;
}

function LargePreview({ docId, filename }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [scale, setScale] = useState(1);
  const isPdf = (filename || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    let revoked = null;
    setErr(false); setUrl(null); setScale(1);
    const token = localStorage.getItem("token");
    fetch(`${API}/documents/${docId}/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("load"); return r.blob(); })
      .then((b) => { revoked = URL.createObjectURL(b); setUrl(revoked); })
      .catch(() => setErr(true));
    return () => revoked && URL.revokeObjectURL(revoked);
  }, [docId]);

  const openFull = () => {
    const token = localStorage.getItem("token");
    fetch(`${API}/documents/${docId}/file`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => {});
  };
  const zoomIn = () => setScale((s) => Math.min(s + 0.25, 4));
  const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5));
  const reset = () => setScale(1);

  if (err) {
    return (
      <div className="w-full h-[76vh] rounded-md border border-amber-200 bg-amber-50 flex flex-col items-center justify-center gap-2 p-6 text-center" data-testid="review-preview-error">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
        <div className="font-medium text-amber-800">Bestand niet gevonden</div>
        <p className="text-sm text-amber-700 max-w-sm">Dit Z-ticket kon niet geladen worden. Verwijder deze regel en upload of mail het ticket opnieuw.</p>
      </div>
    );
  }
  if (!url) return <div className="w-full h-[76vh] rounded-md bg-muted animate-pulse" />;
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={zoomOut} data-testid="review-zoom-out" title="Uitzoomen"><ZoomOut className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={zoomIn} data-testid="review-zoom-in" title="Inzoomen"><ZoomIn className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" className="h-8" onClick={reset} data-testid="review-zoom-reset"><Maximize2 className="h-3.5 w-3.5 mr-1.5" /> Passend</Button>
        <span className="text-xs text-muted-foreground tabular-nums ml-1">{Math.round(scale * 100)}%</span>
        <Button variant="ghost" size="sm" className="h-8 ml-auto" onClick={openFull} data-testid="review-open-full"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> {isPdf ? "PDF in nieuw tabblad" : "Ware grootte"}</Button>
      </div>
      <div className="w-full h-[76vh] overflow-auto rounded-md border border-border bg-white" data-testid="review-preview-scroll">
        <img
          src={url}
          alt="Z-ticket"
          data-testid="review-preview-img"
          onClick={zoomIn}
          style={{ width: `${scale * 100}%` }}
          className="block max-w-none cursor-zoom-in"
        />
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">Zoom met de knoppen (of klik op de bon) en scroll om alle regels te lezen.</p>
    </div>
  );
}

export default function ZTicketInbox() {
  const { user, logout, canWrite } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState([]);
  const [processed, setProcessed] = useState([]);
  const [review, setReview] = useState(null);
  const [cats, setCats] = useState({});
  const [rDate, setRDate] = useState("");
  const [reviewBooked, setReviewBooked] = useState(null);
  const [reviewLoc, setReviewLoc] = useState("");
  const [saving, setSaving] = useState(false);

  // e-mail ingest
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: "", app_password: "", imap_host: "imap.gmail.com", enabled: true, interval_sec: 180 });
  const [savingEmail, setSavingEmail] = useState(false);
  const [checking, setChecking] = useState(false);

  // dagelijkse controle-kopie (23:59)
  const [dailyCfg, setDailyCfg] = useState(null);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [dailyForm, setDailyForm] = useState({ recipient: "", enabled: true });
  const [savingDaily, setSavingDaily] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);

  const loadLists = useCallback(() => {
    api.get("/ztickets/inbox", { params: { status: "pending" } }).then((r) => setPending(r.data));
    api.get("/ztickets/inbox", { params: { status: "processed" } }).then((r) => setProcessed(r.data));
  }, []);
  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)); loadLists(); loadEmailCfg(); loadDailyCfg(); }, [loadLists]);

  const loadDailyCfg = () => {
    api.get("/ztickets/daily-report/config").then((r) => {
      setDailyCfg(r.data);
      setDailyForm({ recipient: r.data.recipient || "", enabled: r.data.enabled ?? true });
    }).catch(() => {});
  };

  const saveDailyCfg = async () => {
    setSavingDaily(true);
    try {
      await api.post("/ztickets/daily-report/config", dailyForm);
      toast.success("Instellingen dagmail opgeslagen");
      setDailyOpen(false); loadDailyCfg();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingDaily(false); }
  };

  const sendDailyNow = async () => {
    setSendingDaily(true);
    try {
      const { data } = await api.post("/ztickets/daily-report/test");
      if (data.errors?.length) toast.error(data.errors[0]);
      else toast.success(`${data.sent} mail(s) verstuurd`);
      loadDailyCfg();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingDaily(false); }
  };

  const loadEmailCfg = () => {
    api.get("/ztickets/email/config").then((r) => {
      setEmailCfg(r.data);
      setEmailForm((f) => ({ ...f, email: r.data.email || "", imap_host: r.data.imap_host || "imap.gmail.com",
        enabled: r.data.enabled ?? true, interval_sec: r.data.interval_sec || 180, app_password: "" }));
    }).catch(() => {});
  };

  const saveEmailCfg = async () => {
    if (!emailForm.email) return toast.error("Vul het e-mailadres in");
    setSavingEmail(true);
    try {
      await api.post("/ztickets/email/config", emailForm);
      toast.success("E-mailinstellingen opgeslagen");
      loadEmailCfg();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingEmail(false); }
  };

  const checkEmailNow = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/ztickets/email/check");
      if (data.errors?.length) toast.error(data.errors[0]);
      else toast.success(`${data.processed} nieuw(e) bon(nen) opgehaald, ${data.skipped} overgeslagen`);
      loadLists(); loadEmailCfg();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const upload = async () => {
    if (!locId) return toast.error("Kies eerst een winkel");
    if (!file) return toast.error("Kies of maak een foto van het Z-ticket");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("location_id", locId);
      fd.append("date", date);
      const { data } = await api.post("/ztickets/inbox", fd);
      const shop = data.location_name || "winkel";
      if (data.date && data.date !== date) {
        toast.info(`Datum van het ticket herkend: ${data.date} — controleer bij het boeken`);
      }
      if (data.duplicate?.already_booked) {
        toast.warning(`Let op: voor ${shop} op ${data.date} is al een dagontvangst geboekt (dubbele dag).`);
      } else if (data.duplicate?.in_inbox) {
        toast.warning(`Let op: er staat al een Z-ticket voor ${shop} op ${data.date} in de wachtrij.`);
      } else {
        toast.success("Z-ticket opgeslagen — klaar om te verwerken");
      }
      setFile(null);
      loadLists();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/ztickets/inbox/${id}`); toast.success("Verwijderd"); loadLists(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openReview = (item) => {
    setReview(item);
    setCats({ ...(item.proposal?.categories || {}) });
    setRDate(item.date || item.proposal?.date || new Date().toISOString().slice(0, 10));
    setReviewLoc(item.location_id || item.proposal?.matched_location_id || "");
  };

  // live check: is there already a dagontvangst for this shop + date? (duplicate day warning)
  useEffect(() => {
    if (!review || !rDate || !reviewLoc) { setReviewBooked(null); return; }
    api.get("/journal-check", { params: { location_id: reviewLoc, date: rDate } })
      .then((r) => setReviewBooked(r.data.exists ? r.data : null))
      .catch(() => setReviewBooked(null));
  }, [review, rDate, reviewLoc]);

  const gross_by_rate = RATE_CODES.reduce((acc, c) => ({ ...acc, [c]: 0 }), {});
  Object.entries(cats).forEach(([cat, amt]) => {
    const bucket = CATEGORY_TO_BUCKET[cat] || "21";
    gross_by_rate[bucket] = Math.round((gross_by_rate[bucket] + (parseFloat(amt) || 0)) * 100) / 100;
  });
  const catSum = Math.round(Object.values(cats).reduce((s, v) => s + (parseFloat(v) || 0), 0) * 100) / 100;
  const zTotal = review?.proposal?.z_total || 0;
  const reconciled = zTotal ? Math.abs(catSum - zTotal) < 0.05 : true;

  const confirm = async () => {
    if (!reviewLoc) return toast.error("Kies eerst een winkel voor deze bon");
    setSaving(true);
    try {
      const gbr = {};
      RATE_CODES.forEach((c) => (gbr[c] = gross_by_rate[c] || 0));
      await api.post(`/ztickets/inbox/${review.id}/confirm`, {
        date: rDate, categories: cats, gross_by_rate: gbr, location_id: reviewLoc,
        note: `Geboekt via Z-ticket (gsm)${review.proposal?.z_number ? " (Z" + review.proposal.z_number + ")" : ""}`,
      });
      toast.success("Dagontvangst definitief geboekt");
      setReview(null); loadLists();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const catLabels = review?.proposal?.category_labels || {};

  return (
    <div className="min-h-screen bg-background" data-testid="zinbox-page">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground shrink-0">
            <Building2 className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-sm">Z-tickets uploaden</div>
            <div className="text-xs text-muted-foreground">Singh Express Shop BV</div>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="sm" data-testid="zinbox-to-journal" onClick={() => navigate("/journal")}>
              <BookText className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Dagboek</span>
            </Button>
            <Button variant="ghost" size="sm" data-testid="zinbox-to-hub" onClick={() => navigate("/hub")}>
              <Grid2x2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Modules</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ScanLine className="h-7 w-7 text-primary" /> Z-tickets
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload per winkel en per dag de dagontvangst. Op de gsm foto's maken, later op de laptop nakijken en boeken.
          </p>
        </div>

        {canWrite && (
          <Card className="shadow-sm">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> Nieuwe upload</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Winkel</Label>
                  <Select value={locId} onValueChange={setLocId}>
                    <SelectTrigger data-testid="zinbox-location"><SelectValue placeholder="Kies winkel" /></SelectTrigger>
                    <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Datum</Label>
                  <Input type="date" data-testid="zinbox-date" value={date} onChange={(e) => setDate(e.target.value)} />
                  <p className="text-xs text-muted-foreground">De datum van het ticket wordt automatisch herkend; dit is de reservekeuze.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-5 text-center cursor-pointer hover:border-primary transition-colors">
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                         data-testid="zinbox-camera" onChange={(e) => e.target.files[0] && setFile(e.target.files[0])} />
                  <Camera className="h-7 w-7 mb-2 text-primary" />
                  <div className="text-sm font-medium">Foto maken</div>
                  <div className="text-xs text-muted-foreground">met de camera</div>
                </label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-5 text-center cursor-pointer hover:border-primary transition-colors">
                  <input type="file" accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.heic,.webp" className="hidden"
                         data-testid="zinbox-file" onChange={(e) => e.target.files[0] && setFile(e.target.files[0])} />
                  <Upload className="h-7 w-7 mb-2 text-primary" />
                  <div className="text-sm font-medium">Bestand kiezen</div>
                  <div className="text-xs text-muted-foreground">foto of PDF uit uw bestanden</div>
                </label>
              </div>
              {file && (
                <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm" data-testid="zinbox-selected">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <span className="truncate">{file.name}</span>
                  <button type="button" className="ml-auto text-xs text-muted-foreground hover:text-destructive" onClick={() => setFile(null)}>verwijderen</button>
                </div>
              )}
              <Button className="w-full" onClick={upload} disabled={uploading} data-testid="zinbox-upload-btn">
                {uploading ? "AI leest ticket uit…" : "Opslaan als te verwerken"}
              </Button>
            </CardContent>
          </Card>
        )}

        {canWrite && (
          <Card className="shadow-sm border-dashed">
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <Mail className="h-5 w-5 text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm flex items-center gap-2">
                  Z-tickets via e-mail
                  {emailCfg?.configured && emailCfg?.enabled && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${emailCfg?.automation_active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="email-automation-status">
                      {emailCfg?.automation_active ? "Automatisch actief" : "Alleen handmatig (test)"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {emailCfg?.configured && emailCfg?.enabled
                    ? <>Actief op <b>{emailCfg.email}</b>{emailCfg.last_result ? ` · laatst: ${emailCfg.last_result}` : ""}{emailCfg.last_error ? ` · fout: ${emailCfg.last_error}` : ""}</>
                    : "Stuur uw Z-tickets als bijlage naar een mailbox; ze verschijnen automatisch bij 'Te verwerken'."}
                </div>
              </div>
              <div className="flex gap-2">
                {emailCfg?.configured && emailCfg?.enabled && (
                  <Button variant="outline" size="sm" onClick={checkEmailNow} disabled={checking} data-testid="email-check-now">
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${checking ? "animate-spin" : ""}`} /> Nu controleren
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => {
                  if (emailCfg?.configured) setEmailForm({ email: emailCfg.email || "", app_password: "",
                    imap_host: emailCfg.imap_host || "imap.gmail.com", enabled: emailCfg.enabled ?? true,
                    interval_sec: emailCfg.interval_sec || 180 });
                  setEmailOpen(true);
                }} data-testid="email-settings-open">
                  <Settings className="h-4 w-4 mr-1.5" /> Instellen
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {canWrite && (
          <Card className="shadow-sm border-dashed">
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <CalendarClock className="h-5 w-5 text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">Brief FOD bij bevestiging</div>
                <div className="text-xs text-muted-foreground">
                  {dailyCfg?.enabled
                    ? <>Bij élke bevestigde dagontvangst gaat de brief + Z-ticket direct naar <b>{dailyCfg.recipient}</b>. Om 23:59 volgt enkel een herinnering voor dagen zonder boeking.{dailyCfg.last_result ? ` · laatst: ${dailyCfg.last_result}` : ""}</>
                    : "Automatische mail staat uit. Zet aan om bij elke bevestiging de brief voor FOD Financiën te ontvangen."}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={sendDailyNow} disabled={sendingDaily} data-testid="daily-send-now">
                  <Send className={`h-4 w-4 mr-1.5 ${sendingDaily ? "animate-pulse" : ""}`} /> Test: herinnering gisteren
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDailyOpen(true)} data-testid="daily-settings-open">
                  <Settings className="h-4 w-4 mr-1.5" /> Instellen
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="pending">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="h-4 w-4 mr-1.5" /> Te verwerken {pending.length > 0 && <Badge className="ml-1.5 bg-amber-100 text-amber-700 hover:bg-amber-100">{pending.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="processed" data-testid="tab-processed">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Verwerkt {processed.length > 0 && <Badge className="ml-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{processed.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-3 mt-4" data-testid="pending-list">
            {pending.length === 0 && <p className="text-center text-muted-foreground py-10 text-sm">Geen te verwerken Z-tickets.</p>}
            {pending.map((item) => (
              <Card key={item.id} className="shadow-sm" data-testid={`pending-${item.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Thumb docId={item.z_document_id} filename={item.filename} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-muted-foreground" />{item.location_name}</div>
                    <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      <span className="font-mono">{item.date || "—"}</span>
                      <span>Z-totaal: <b className="tabular-nums">{eur(item.proposal?.z_total)}</b></span>
                      <Badge variant="outline" className={conf[item.proposal?.confidence] || ""}>{item.proposal?.confidence || "—"}</Badge>
                      {item.already_booked && (
                        <Badge variant="outline" className="text-red-600 border-red-300 gap-1" data-testid={`dup-badge-${item.id}`}>
                          <AlertTriangle className="h-3 w-3" /> Dag al geboekt
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    {canWrite && <Button size="sm" onClick={() => openReview(item)} data-testid={`review-${item.id}`}>Controleer & boek</Button>}
                    {canWrite && <Button size="icon" variant="ghost" onClick={() => remove(item.id)} data-testid={`delete-${item.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="processed" className="mt-4" data-testid="processed-list">
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Winkel</TableHead><TableHead className="text-right">Z-totaal</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {processed.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nog niets verwerkt.</TableCell></TableRow>}
                    {processed.map((item) => (
                      <TableRow key={item.id} data-testid={`processed-${item.id}`}>
                        <TableCell className="font-mono text-sm">{item.date}</TableCell>
                        <TableCell className="font-medium">{item.location_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(item.proposal?.z_total)}</TableCell>
                        <TableCell><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" /> Geboekt</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-7xl w-[96vw] max-h-[94vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Controleer en boek — {review?.location_name}</DialogTitle>
            <DialogDescription>Controleer het AI-voorstel en bevestig om definitief in het dagboek te boeken. Klik op de bon om in/uit te zoomen.</DialogDescription>
          </DialogHeader>
          {review && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <div className="md:col-span-3">
                <Label className="text-xs text-muted-foreground">Z-ticket</Label>
                <div className="mt-1.5 rounded-lg border border-border bg-muted/30 p-2 md:sticky md:top-2">
                  <LargePreview docId={review.z_document_id} filename={review.filename} />
                </div>
              </div>
              <div className="md:col-span-2 space-y-4">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Winkel</Label>
                  <Select value={reviewLoc} onValueChange={setReviewLoc}>
                    <SelectTrigger data-testid="review-location" className={!reviewLoc ? "border-amber-400" : ""}><SelectValue placeholder="Kies winkel" /></SelectTrigger>
                    <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {!reviewLoc && <p className="text-xs text-amber-600">Winkel niet automatisch herkend — kies de juiste winkel.</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Datum</Label>
                  <Input type="date" data-testid="review-date" value={rDate} onChange={(e) => setRDate(e.target.value)} />
                  {review.proposal?.date && (
                    <p className="text-xs text-muted-foreground">Voorgestelde datum van het ticket: <b>{review.proposal.date}</b></p>
                  )}
                </div>
                {reviewBooked && (
                  <div className="flex items-start gap-2 rounded-md px-3 py-2 text-sm bg-red-50 text-red-700 border border-red-200" data-testid="review-already-booked">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Dubbele dagontvangst: voor <b>{review.location_name}</b> is op <b>{rDate}</b> al een dagontvangst geboekt ({eur(reviewBooked.total_gross)}). Een tweede boeking op dezelfde dag wordt geweigerd — kies een andere datum of gebruik een correctie.</span>
                  </div>
                )}
                <div>
                  <Label className="text-sm">Categorie-overzicht</Label>
                  <Table className="mt-1.5">
                    <TableHeader><TableRow><TableHead>Categorie</TableHead><TableHead className="text-right">Bedrag incl.</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {Object.keys(cats).map((cat) => (
                        <TableRow key={cat}>
                          <TableCell className="font-medium">{catLabels[cat] || cat}</TableCell>
                          <TableCell className="text-right">
                            <Input type="number" step="0.01" className="w-28 ml-auto text-right tabular-nums h-8"
                                   data-testid={`review-cat-${cat}`} value={cats[cat]}
                                   onChange={(e) => setCats({ ...cats, [cat]: e.target.value })} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${reconciled ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="review-reconcile">
                  {reconciled ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                  Z-totaal: <b>{eur(zTotal)}</b> · Som: <b>{eur(catSum)}</b>{reconciled ? " ✓" : " — wijkt af"}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={confirm} disabled={saving || !!reviewBooked || !reviewLoc} data-testid="review-confirm">
              <CheckCircle2 className="h-4 w-4 mr-2" />{saving ? "Boeken…" : "Bevestigen en definitief boeken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-mail settings dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Z-tickets via e-mail instellen</DialogTitle>
            <DialogDescription>
              Stuur uw Z-tickets als bijlage (foto of PDF) naar deze mailbox. Zet in het onderwerp <b>"Z"</b>, de <b>winkel</b> en de <b>datum</b>, bv. <b>"Z Brustem 24/07/2026"</b>. Enkel mails met die drie worden door de AI uitgelezen — zo worden er geen credits verspild aan andere bijlagen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs p-3 space-y-1">
              <p className="font-medium">Gmail-app-wachtwoord aanmaken:</p>
              <p>1. Zet 2-stapsverificatie aan op het Google-account. 2. Ga naar Google-account → Beveiliging → App-wachtwoorden. 3. Maak een app-wachtwoord ("Mail") en plak dat hieronder (niet uw gewone wachtwoord). 4. Zet in Gmail IMAP aan (Instellingen → Doorsturen en POP/IMAP).</p>
            </div>
            <div className="space-y-1.5"><Label>E-mailadres (mailbox)</Label>
              <Input data-testid="email-address" placeholder="singhexpress68@gmail.com" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>App-wachtwoord</Label>
              <Input type="password" data-testid="email-password" placeholder={emailCfg?.has_password ? "•••••••• (ongewijzigd laten)" : "16-tekens app-wachtwoord"} value={emailForm.app_password} onChange={(e) => setEmailForm({ ...emailForm, app_password: e.target.value })} />
              <p className="text-xs text-muted-foreground">Wordt versleuteld bewaard en nooit getoond. Laat leeg om het huidige te behouden.</p></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>IMAP-server</Label>
                <Input data-testid="email-host" value={emailForm.imap_host} onChange={(e) => setEmailForm({ ...emailForm, imap_host: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Interval (sec)</Label>
                <Input type="number" min="60" data-testid="email-interval" value={emailForm.interval_sec} onChange={(e) => setEmailForm({ ...emailForm, interval_sec: parseInt(e.target.value || "180", 10) })} /></div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div><div className="font-medium text-sm">Automatisch ophalen</div><div className="text-xs text-muted-foreground">Controleert de mailbox periodiek</div></div>
              <Switch checked={emailForm.enabled} onCheckedChange={(v) => setEmailForm({ ...emailForm, enabled: v })} data-testid="email-enabled" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {emailCfg?.configured && (
              <Button variant="outline" onClick={checkEmailNow} disabled={checking} data-testid="email-check-now-dialog">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${checking ? "animate-spin" : ""}`} /> Nu controleren
              </Button>
            )}
            <Button onClick={saveEmailCfg} disabled={savingEmail} data-testid="email-save">
              {savingEmail ? "Bezig…" : "Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dagelijkse controle-kopie instellen */}
      <Dialog open={dailyOpen} onOpenChange={setDailyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Brief FOD bij bevestiging</DialogTitle>
            <DialogDescription>
              Telkens je een dagontvangst bevestigt, wordt meteen de begeleidende brief + de Z-ticket per e-mail verstuurd — een kopie die je kunt voorleggen bij een controle van FOD Financiën. Om 23:59 (Belgische tijd) stuurt het systeem enkel nog een herinnering voor dagen zonder boeking (Sint-Truiden en Brustem: elke dag; Leesberg: niet op zon- en feestdagen).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Ontvanger (e-mailadres)</Label>
              <Input data-testid="daily-recipient" placeholder="naam@voorbeeld.be" value={dailyForm.recipient}
                     onChange={(e) => setDailyForm({ ...dailyForm, recipient: e.target.value })} />
              <p className="text-xs text-muted-foreground">Meerdere adressen? Scheid ze met een komma.</p>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div><div className="font-medium text-sm">Automatisch versturen</div><div className="text-xs text-muted-foreground">Brief + Z-ticket bij elke bevestiging</div></div>
              <Switch checked={dailyForm.enabled} onCheckedChange={(v) => setDailyForm({ ...dailyForm, enabled: v })} data-testid="daily-enabled" />
            </div>
            {dailyCfg?.last_run && (
              <div className="text-xs text-muted-foreground">Laatste verzending: {dailyCfg.last_target_date || "—"} · {dailyCfg.last_result || "—"}</div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={sendDailyNow} disabled={sendingDaily} data-testid="daily-send-now-dialog">
              <Send className={`h-4 w-4 mr-1.5 ${sendingDaily ? "animate-pulse" : ""}`} /> Nu versturen
            </Button>
            <Button onClick={saveDailyCfg} disabled={savingDaily} data-testid="daily-save">
              {savingDaily ? "Bezig…" : "Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
